/**
 * Le compte d'une famille, lu sans quitter le rapprochement.
 *
 * ## Pourquoi ce panneau existe
 *
 * Demandé le 18 septembre 2026 : « la possibilité de voir tt le détail des
 * factures, notes de crédits et versement pour analyser au mieux la situation
 * du compte ».
 *
 * Jusqu'ici, juger un virement obligeait à ouvrir l'écran des factures dans un
 * autre onglet, y recopier un numéro, revenir — et recommencer pour chaque
 * famille citée. Un virement qui cite cinq factures de deux familles
 * demandait donc une dizaine d'allers-retours, en tenant les montants de tête.
 *
 * ## Ce que ce panneau montre, et pourquoi chaque colonne y est
 *
 * - **Les factures annulées et créditées y sont**, marquées comme telles. Les
 *   masquer ferait disparaître la raison même pour laquelle une famille a un
 *   excédent — c'est-à-dire la question posée.
 * - **Les notes de crédit sont nommées et cliquables.** « 60,00 € dont 30,00 €
 *   de note de crédit » ne se vérifie pas ; `NC-25-00034` s'ouvre.
 * - **Les versements montrent ce que la ligne a placé ET ce que le virement
 *   portait.** Un virement de 180,00 € dont 20,00 € sont venus ici n'est pas
 *   un virement de 20,00 €, et les confondre fait chercher 160,00 € qui ne
 *   manquent pas.
 * - **Les lignes du virement examiné sont surlignées.** Sans cela, une facture
 *   soldée à l'instant par ce virement se lit « déjà payée par ailleurs », et
 *   l'administration défait ce qu'elle vient de faire.
 * - **Les virements de cette famille ENCORE EN FILE y sont aussi.** Ajouté le
 *   18 septembre 2026, sur un compte où l'administration comptait trois
 *   versements de 98,00 € et le panneau semblait en connaître quatre. Le
 *   quatrième existait : il était en file, et le compte n'en disait rien.
 *   Mesuré en production : 271 familles, 1 048 virements, 43 529,17 €.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Inbox, Loader2, Wallet } from 'lucide-react';
import clsx from 'clsx';
import { lireCompteFamille, type CompteFamille } from '../../../lib/payments/situation.supabase';
import { messageErreur } from '../../../lib/payments/erreur';
import { ETATS, type Settlement } from '../../../lib/factures/types';
import LienFacture from './LienFacture';
import { dateCourte, euros } from './money';

export default function CompteDeLaFamille({
  userId, paiementExamine, ibanExamine,
}: {
  userId: string;
  /** Le virement en cours d'examen : ses lignes seront surlignées. */
  paiementExamine?: string;
  /** Son compte bancaire : il entre dans la recherche des virements en file. */
  ibanExamine?: string | null;
}) {
  const [compte, setCompte] = useState<CompteFamille | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    setCompte(null);
    setErreur(null);
    (async () => {
      try {
        const c = await lireCompteFamille(userId, paiementExamine, ibanExamine);
        if (!annule) setCompte(c);
      } catch (e) {
        if (!annule) setErreur(messageErreur(e, 'Lecture du compte impossible'));
      }
    })();
    return () => { annule = true; };
  }, [userId, paiementExamine, ibanExamine]);

  if (erreur) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{erreur}</span>
      </p>
    );
  }

  if (!compte) {
    return (
      <p className="flex items-center gap-2 py-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Lecture du compte…
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium text-gray-900">
          {compte.famille}
          {compte.courriel && (
            <span className="ml-2 text-xs font-normal text-gray-500">{compte.courriel}</span>
          )}
        </p>
        <p className="flex flex-wrap items-baseline gap-x-3 text-sm tabular-nums text-gray-700">
          {compte.resteDuCents > 0
            ? <span className="font-medium text-amber-800">{euros(compte.resteDuCents)} encore dus</span>
            : <span className="text-green-800">Rien à réclamer</span>}
          {compte.enFileCents > 0 && (
            <span className="font-medium text-blue-800">
              {euros(compte.enFileCents)} encore en file
            </span>
          )}
        </p>
      </div>

      {/*
        L'arithmétique que l'administration faisait de tête, et qui l'a induite
        en erreur le 18 septembre 2026 : « j'ai reçu 218 et on a facturé − ndc
        = 218, donc ça devrait être à l'équilibre ». C'était vrai des FACTURES,
        et faux du COMPTE — 35,00 € de plus étaient arrivés en banque, et ils
        n'étaient sur aucune facture. La soustraction est donc faite ici.
      */}
      {compte.enFileCents > 0 && <Solde compte={compte} />}

      {compte.avoir && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded bg-indigo-50 px-2 py-1.5 text-xs text-indigo-900">
          <span className="flex items-center gap-1 font-medium">
            <Wallet className="h-3.5 w-3.5" /> Avoir : {euros(compte.avoir.avoirCents)}
          </span>
          {/*
            Le disponible est VERROUILLÉ depuis le 18 septembre 2026 : tant que
            `provision_auto` vaut `off`, il rend zéro quel que soit l'avoir
            possédé. Les deux se lisent donc ensemble — n'afficher que le
            disponible ferait croire que l'argent n'existe pas.
          */}
          <span>dont {euros(compte.avoir.disponibleCents)} utilisables aujourd'hui</span>
          {compte.avoir.enAttenteCents > 0 && (
            <span>· {euros(compte.avoir.enAttenteCents)} en attente de virement</span>
          )}
          {compte.avoir.etat && <span className="text-indigo-700">· compte {compte.avoir.etat}</span>}
        </p>
      )}

      <Factures compte={compte} />
      <Versements compte={compte} />
      <EnFile compte={compte} />

      {compte.tronque && (
        <p className="text-xs text-amber-800">
          Ce compte dépasse ce que ce panneau affiche : l'écran des factures en montre la
          totalité. Rien n'est tronqué en silence — cette phrase est là pour le dire.
        </p>
      )}
    </div>
  );
}

/**
 * Où en serait ce compte une fois la file placée.
 *
 * Une seule soustraction — `encore en file` moins `encore dû` — mais c'est
 * celle que l'administration faisait de tête, et sur laquelle elle s'est
 * trompée : elle lisait « reçu » dans le tableau des factures, qui ne compte
 * que l'argent DÉJÀ PLACÉ. Les virements restés en file n'y sont pas, et
 * l'équilibre paraissait donc atteint alors qu'il manquait 35,00 € de plus
 * arrivés en banque.
 */
function Solde({ compte }: { compte: CompteFamille }) {
  const solde = compte.enFileCents - compte.resteDuCents;
  /* Un virement lu deux fois n'est pas de l'argent en plus : le compter ici
     annonçait « 173,00 € de trop » à une famille qui avait payé une fois
     (27/09/2026). On le dit, au lieu de le soustraire en silence. */
  const luDeuxFois = compte.enFile.filter((v) => v.luDeuxFois).reduce((s, v) => s + v.resteCents, 0);

  return (
    <p className="rounded bg-blue-50 px-2 py-1.5 text-xs text-blue-900">
      {luDeuxFois > 0 && (
        <>
          <strong>Attention :</strong> {euros(luDeuxFois)} en file sont très probablement un virement déjà
          enregistré, lu une seconde fois — à réunir depuis la file, pas à placer.{' '}
        </>
      )}
      {solde > 0 && (
        <>
          Une fois la file placée, cette famille aurait <strong>{euros(solde)} de trop</strong> —
          à mettre en provision ou à rembourser.
        </>
      )}
      {solde < 0 && (
        <>
          Même en plaçant tout ce qui est en file, il resterait <strong>{euros(-solde)}</strong> à
          réclamer à cette famille.
        </>
      )}
      {solde === 0 && <>Ce qui est en file solde exactement ce que cette famille doit encore.</>}
    </p>
  );
}

function Factures({ compte }: { compte: CompteFamille }) {
  if (compte.factures.length === 0) {
    return <p className="text-sm text-gray-600">Aucune facture pour ce compte.</p>;
  }

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Factures ({compte.factures.length})
      </p>
      <div className="mt-1 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1 text-left font-medium">Facture</th>
              <th className="py-1 text-left font-medium">Échéance</th>
              <th className="py-1 text-left font-medium">État</th>
              <th className="py-1 text-right font-medium">Montant</th>
              <th className="py-1 text-right font-medium">Note de crédit</th>
              <th className="py-1 text-right font-medium">Reçu</th>
              <th className="py-1 text-right font-medium">Reste</th>
            </tr>
          </thead>
          <tbody>
            {compte.factures.map((f) => {
              const etat = ETATS[f.settlement as Settlement];
              return (
                <tr key={f.invoiceId} className="border-t border-gray-100 align-top">
                  <td className="py-1.5">
                    <LienFacture invoiceNumber={f.invoiceNumber} pdfUrl={f.pdfUrl} />
                    {f.notes.length > 0 && (
                      <ul className="mt-0.5 space-y-0.5">
                        {f.notes.map((n) => (
                          <li key={n.creditNoteId} className="text-xs text-indigo-800">
                            <LienFacture invoiceNumber={n.numero} pdfUrl={n.pdfUrl} />
                            <span className="ml-1 tabular-nums">−{euros(n.montantCents)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-1.5 text-xs text-gray-500">{dateCourte(f.echeanceLe)}</td>
                  <td className="py-1.5">
                    <span className={clsx(
                      'inline-block rounded px-1.5 py-0.5 text-xs font-medium',
                      etat?.classe ?? 'bg-gray-100 text-gray-700',
                    )}>
                      {etat?.label ?? f.settlement}
                    </span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-700">{euros(f.factureCents)}</td>
                  <td className="py-1.5 text-right tabular-nums text-indigo-800">
                    {f.notesCents > 0 ? `−${euros(f.notesCents)}` : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-gray-700">{euros(f.recuCents)}</td>
                  <td className={clsx(
                    'py-1.5 text-right tabular-nums',
                    f.settlement === 'cancelled' ? 'text-gray-400'
                      : f.resteCents > 0 ? 'font-medium text-amber-800' : 'text-gray-500',
                  )}>
                    {euros(f.resteCents)}
                    {f.tropPercuCents > 0 && (
                      <span className="block text-xs text-indigo-700">
                        +{euros(f.tropPercuCents)} de trop
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Les virements de cette famille qui ne sont placés nulle part.
 *
 * ## Pourquoi cette section existe
 *
 * Signalé le 18 septembre 2026 : « moi dans ma compta j'ai 3 paiements qui
 * sont arrivés pour ce compte... si je comprends bien il trouve 4 x 98 euros
 * pour ce compte ». Le panneau annonçait « Versements reçus (3) » et s'arrêtait
 * là. Le quatrième virement de 98,00 € existait bel et bien — il était en file,
 * et le compte n'en disait rien.
 *
 * **Un compte qui ne montre qu'une partie de ce qu'une famille a versé est pire
 * qu'un compte sans versements** : on en tire un solde, et le solde est faux.
 * Mesuré en production : 271 familles, 1 048 virements, 43 529,17 €.
 */
function EnFile({ compte }: { compte: CompteFamille }) {
  if (compte.enFile.length === 0) return null;

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/60 p-2">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-blue-800">
        <Inbox className="h-3.5 w-3.5" />
        Encore à placer ({compte.enFile.length}) — {euros(compte.enFileCents)}
      </p>
      <p className="mt-1 text-xs text-blue-900">
        Des virements venus d'un compte bancaire connu de cette famille, qu'aucune facture n'a
        encore reçus. Ils comptent dans ce que la famille a versé, même s'ils ne comptent nulle
        part ailleurs.
      </p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-blue-700">
            <tr>
              <th className="py-1 text-left font-medium">Date</th>
              <th className="py-1 text-right font-medium">Virement</th>
              <th className="py-1 text-right font-medium">À placer</th>
              <th className="py-1 text-left font-medium">Communication</th>
            </tr>
          </thead>
          <tbody>
            {compte.enFile.map((v) => (
              <tr key={v.paymentId} className="border-t border-blue-100 align-top">
                <td className="py-1.5 whitespace-nowrap text-xs text-blue-900">
                  {dateCourte(v.dateValeur)}
                  {v.examine && (
                    <span className="ml-1 rounded bg-blue-200 px-1 py-0.5 text-[10px] font-medium text-blue-900">
                      celui-ci
                    </span>
                  )}
                  {v.luDeuxFois && (
                    <span className="ml-1 rounded bg-red-100 px-1 py-0.5 text-[10px] font-medium text-red-800">
                      lu deux fois
                    </span>
                  )}
                </td>
                <td className="py-1.5 text-right tabular-nums text-xs text-blue-800">
                  {euros(v.montantCents)}
                </td>
                <td className="py-1.5 text-right tabular-nums font-medium text-blue-900">
                  {euros(v.resteCents)}
                </td>
                <td className="py-1.5 text-xs text-blue-800">
                  <span className="break-words">{v.communication?.trim() || '—'}</span>
                  {v.contrepartie && (
                    <span className="block text-blue-500">{v.contrepartie}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Ce qu'une ligne d'affectation a payé, en français. */
function cibleLisible(v: CompteFamille['versements'][number]): string {
  if (v.invoiceNumber) return v.invoiceNumber;
  if (v.cible === 'provision') return 'Provision';
  if (v.cible === 'out_of_scope') return 'Hors périmètre';
  return v.cible;
}

function Versements({ compte }: { compte: CompteFamille }) {
  if (compte.versements.length === 0) {
    return <p className="text-sm text-gray-600">Aucun versement reçu sur ce compte.</p>;
  }

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Versements reçus ({compte.versements.length})
      </p>
      <div className="mt-1 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1 text-left font-medium">Date</th>
              <th className="py-1 text-right font-medium">Placé ici</th>
              <th className="py-1 text-right font-medium">Virement</th>
              <th className="py-1 text-left font-medium">Sur</th>
              <th className="py-1 text-left font-medium">Communication</th>
            </tr>
          </thead>
          <tbody>
            {compte.versements.map((v) => (
              <tr
                key={v.allocationId}
                className={clsx(
                  'border-t border-gray-100 align-top',
                  v.deCeVirement && 'bg-blue-50',
                )}
              >
                <td className="py-1.5 whitespace-nowrap text-xs text-gray-600">
                  {dateCourte(v.dateValeur)}
                  {v.deCeVirement && (
                    <span className="ml-1 rounded bg-blue-100 px-1 py-0.5 text-[10px] font-medium text-blue-900">
                      ce virement
                    </span>
                  )}
                </td>
                <td className="py-1.5 text-right tabular-nums font-medium text-gray-900">
                  {euros(v.montantCents)}
                </td>
                <td className="py-1.5 text-right tabular-nums text-xs text-gray-500">
                  {v.virementCents > 0 ? euros(v.virementCents) : '—'}
                </td>
                <td className="py-1.5 text-xs text-gray-700">{cibleLisible(v)}</td>
                <td className="py-1.5 text-xs text-gray-500">
                  <span className="break-words">{v.communication?.trim() || '—'}</span>
                  {v.contrepartie && (
                    <span className="block text-gray-400">{v.contrepartie}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
